import { useEffect, useState } from 'react';
import styled from 'styled-components';

import {
  Accordion,
  AccordionSection,
  Action,
  ActionGroup,
  Text,
  breakpoints,
  Size,
  Skeleton,
} from '@asurion-core/mx-asurion-ui-react';

import { type Actions as FabActions } from '@automation-suite/shared-clients-widget-variant-fab';
import { type Actions as FacActions } from '@automation-suite/shared-clients-widget-variant-fac';
import { type Actions as FicActions } from '@automation-suite/shared-clients-widget-variant-fic';
import { type Actions as EmbeddedActions } from '@automation-suite/shared-clients-widget-variant-embedded';

import { ContentfulIntegration, OptionEditor, ScriptIntegration, SdkIntegration } from './components';
import {
  initializeFirebaseMessaging,
  isPushNotificationsConfigured,
  requestFcmToken,
} from './clients/pushNotifications';
import { usePlaygroundStore } from './storage';
import { Variant } from '@asurion-core/widget-client/lib/package';
import { VariantOverridesReference } from './components/VariantOverridesReference';
import {
  buildLoadParams,
  findWidgetIdOption,
  orderedVariants,
  variantPresets,
  widgetIdsForEnv,
} from './playgroundConfig';

const PageContainer = styled.div`
  margin: 2rem 1rem;

  ${breakpoints.md} {
    margin: 2rem 2rem;
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin: 2rem 1rem;

  ${breakpoints.md} {
    max-width: ${Size.MD2}px;
    margin: 2rem auto;
  }
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const EmbeddedWidgetContainer = styled.section`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 800px;
  overflow: hidden;
  border: 1px solid #eeedf1;
  border-radius: 0.5em;
`;

const Loader = styled(Skeleton)`
  position: absolute;
  width: 100%;
  height: 100%;
`;

const CustomRenderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  align-items: end;
`;

const SelectField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-family: sans-serif;
  font-size: 0.85rem;
  color: #555;
  min-width: 220px;

  select {
    padding: 0.5rem 0.75rem;
    border: 1px solid #d5d6da;
    border-radius: 0.5rem;
    background: #fff;
    font-size: 0.95rem;
    color: #111;
  }
`;

export const Playground = () => {
  const { value: widget } = usePlaygroundStore((state) => state.widget);
  const widgetParams = usePlaygroundStore((state) => state.widgetParams);
  const setWidgetParams = usePlaygroundStore((state) => state.setWidgetParams);
  const [pushRegistered, setPushRegistered] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  const currentId = widgetParams?.id || undefined;
  const currentVariant = widgetParams?.variant ?? Variant.FLOATING_ACTION_CARD;
  const currentOption = currentId ? findWidgetIdOption(currentId) : undefined;
  const currentIdIsCustom = Boolean(currentId) && !currentOption;

  useEffect(() => {
    if (widgetParams?.pushNotification) {
      initializeFirebaseMessaging();
    }
  }, [widgetParams?.pushNotification]);

  useEffect(() => {
    if (widgetParams?.id) return;
    const fallback = widgetIdsForEnv[0];
    if (!fallback) return;
    setWidgetParams(buildLoadParams(fallback, currentVariant));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleWidgetIdChange = (nextId: string): void => {
    const option = findWidgetIdOption(nextId);
    if (option) setWidgetParams(buildLoadParams(option, currentVariant));
  };

  const handleVariantChange = (nextVariant: Variant): void => {
    if (currentOption) {
      setWidgetParams(buildLoadParams(currentOption, nextVariant));
      return;
    }
    if (widgetParams) setWidgetParams({ ...widgetParams, variant: nextVariant });
  };

  const fireDeepLink = (message: string) => {
    if (widgetParams?.variant === Variant.FLOATING_INPUT_CARD) {
      (widget as FicActions)?.initiateSimplrChat({ message });
    } else if (widgetParams?.variant === Variant.EMBEDDED) {
      (widget as EmbeddedActions)?.initiateChat({ message });
    } else {
      (widget as FacActions)?.initiateChat({ message });
    }
  };

  return (
    <PageContainer>
      <Container>
        <Text size={7} as="h1" weight="heavy">
          Asurion Widget
        </Text>
        <Text size={4} as="h2" weight="feather">
          The Asurion Chat Widget is the reusable SDK and UI platform that powers customer engagement across Asurion's
          digital properties and partner experiences. It enables AI assistance, live Expert support, device activation,
          callback scheduling, VoIP, and other configurable customer journeys through a consistent, scalable, and
          client-agnostic experience.
        </Text>
        <ContentContainer>
          <Text size={4} as="span" weight="heavy">
            Important links
          </Text>

          <ActionGroup>
            <Action
              href="https://www.figma.com/file/pcUzO5gkzold9dxy91zgSS/Chat-Experiences?type=design&node-id=2251%3A6162&mode=design&t=2D75Nd35Fe4IVwzn-1"
              target="_blank"
              variant="flat"
            >
              Figma files
            </Action>
            <Action href="https://asurion.enterprise.slack.com/archives/C03TA6NET7F" target="_blank" variant="flat">
              Find us on slack!
            </Action>
            <Action href="https://widget-docs.prd.aws.service-initiation.asurion.com/" target="_blank" variant="flat">
              Widget docs
            </Action>
          </ActionGroup>
        </ContentContainer>
        <div id="parent-element" />
        {widgetParams?.variant === Variant.EMBEDDED && (
          <EmbeddedWidgetContainer id="embedded-widget-placeholder">
            <Loader height={'100%'} />
          </EmbeddedWidgetContainer>
        )}
        <ContentContainer>
          {widgetIdsForEnv.length > 0 && (
            <>
              <Text size={4}>Widget Shortcuts</Text>
              <Text as="p">Pick any widget id and render it in any variant. Loads automatically on change.</Text>
              <CustomRenderRow>
                <SelectField>
                  Widget ID
                  <select
                    data-testid="widget-shortcut-widget-id-select"
                    value={currentId ?? ''}
                    onChange={(event) => handleWidgetIdChange(event.target.value)}
                  >
                    {currentIdIsCustom && currentId && (
                      <option key={`custom-${currentId}`} value={currentId}>
                        {currentId} (custom)
                      </option>
                    )}
                    {widgetIdsForEnv.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.id}
                      </option>
                    ))}
                  </select>
                </SelectField>
                <SelectField>
                  Variant
                  <select
                    data-testid="widget-shortcut-variant-select"
                    value={currentVariant}
                    onChange={(event) => handleVariantChange(event.target.value as Variant)}
                  >
                    {orderedVariants.map((variant) => (
                      <option key={variant} value={variant}>
                        {variantPresets[variant].label}
                      </option>
                    ))}
                  </select>
                </SelectField>
              </CustomRenderRow>
            </>
          )}
          {widgetParams?.id && (
            <>
              <Text size={3}>Actions</Text>
              <ActionGroup>
                {widgetParams?.variant === Variant.FLOATING_ACTION_BUTTON && (
                  <>
                    <Action color={'primary'} onClick={() => (widget as FabActions)?.show({ displayMode: 'FAB' })}>
                      Floating Action Button
                    </Action>
                    <Action color={'primary'} onClick={() => (widget as FabActions)?.show({ displayMode: 'WINDOW' })}>
                      Chat Panel
                    </Action>
                  </>
                )}
                {widgetParams?.variant === Variant.FLOATING_ACTION_CARD && (
                  <>
                    <Action color={'primary'} onClick={() => (widget as FacActions)?.show({ displayMode: 'FAB' })}>
                      Floating Action Button
                    </Action>
                    <Action color={'primary'} onClick={() => (widget as FacActions)?.show({ displayMode: 'FAC' })}>
                      Floating Action Card
                    </Action>
                    <Action color={'primary'} onClick={() => (widget as FacActions)?.show({ displayMode: 'WINDOW' })}>
                      Chat Panel
                    </Action>
                  </>
                )}
                {widgetParams?.variant === Variant.FLOATING_INPUT_CARD && (
                  <>
                    <Action color={'primary'} onClick={() => (widget as FicActions)?.show({ displayMode: 'FAB' })}>
                      Floating Action Button
                    </Action>
                    <Action color={'primary'} onClick={() => (widget as FicActions)?.show({ displayMode: 'FIC' })}>
                      Floating Input Card
                    </Action>
                    <Action color={'primary'} onClick={() => (widget as FicActions)?.show({ displayMode: 'WINDOW' })}>
                      Chat Panel
                    </Action>
                  </>
                )}
                {isPushNotificationsConfigured && (
                  <Action
                    color="primary"
                    state={pushLoading ? 'loading' : pushRegistered ? 'disabled' : 'enabled'}
                    onClick={async () => {
                      setPushLoading(true);
                      const token = await requestFcmToken();
                      if (token) {
                        await (
                          widget as unknown as {
                            registerForPushNotifications?: (params: {
                              token: string;
                              provider: string;
                            }) => Promise<void>;
                          }
                        )?.registerForPushNotifications?.({ token, provider: 'fcm' });
                        setPushRegistered(true);
                      }
                      setPushLoading(false);
                    }}
                  >
                    {pushRegistered ? 'Registered For Push Notifications' : 'Register For Push Notifications'}
                  </Action>
                )}
              </ActionGroup>
              {currentOption?.deepLinks && currentOption.deepLinks.length > 0 && (
                <>
                  <Text size={3}>Deep Links</Text>
                  <ActionGroup>
                    {currentOption.deepLinks.map((deepLink) => (
                      <Action
                        key={`deep-link-${deepLink.label}`}
                        color="primary"
                        onClick={() => fireDeepLink(deepLink.message)}
                      >
                        {deepLink.label}
                      </Action>
                    ))}
                  </ActionGroup>
                </>
              )}
            </>
          )}
          <Text size={4}>Loader Props</Text>
          <OptionEditor />
        </ContentContainer>
        <ContentContainer>
          <Text size={4}>Variant Option Overrides</Text>
          <Text as="p">
            Reference examples of <i>variantOptionOverrides</i> per variant. Copy any starter template into your
            Contentful config or SDK call, or switch to <i>Full Overrides</i> to see the live overrides for the
            currently loaded widget.
          </Text>
          <VariantOverridesReference />
        </ContentContainer>
        <ContentContainer>
          <Text size={4}>How do I integrate with the widget?</Text>
          <Text as="p">
            Integrating with the widget is as easy as dropping a script into the host app or configuring a new content
            model in Contentful{' '}
            <span aria-label="curtis-fave-emoji" role="img">
              😎
            </span>
          </Text>
          <Accordion variant="full-page">
            <AccordionSection tabIndex={-1}>
              <AccordionSection.Header>
                <AccordionSection.Header.Leading>
                  <AccordionSection.Header.Number>1.</AccordionSection.Header.Number>
                </AccordionSection.Header.Leading>
                <AccordionSection.Header.Heading>
                  Integrating in Contentful (only on asurion.com)
                </AccordionSection.Header.Heading>
              </AccordionSection.Header>
              <ContentfulIntegration />
            </AccordionSection>
            <AccordionSection tabIndex={-1}>
              <AccordionSection.Header>
                <AccordionSection.Header.Leading>
                  <AccordionSection.Header.Number>2.</AccordionSection.Header.Number>
                </AccordionSection.Header.Leading>
                <AccordionSection.Header.Heading>Using the SDK</AccordionSection.Header.Heading>
              </AccordionSection.Header>
              <SdkIntegration />
            </AccordionSection>
            <AccordionSection tabIndex={-1}>
              <AccordionSection.Header>
                <AccordionSection.Header.Leading>
                  <AccordionSection.Header.Number>3.</AccordionSection.Header.Number>
                </AccordionSection.Header.Leading>
                <AccordionSection.Header.Heading>Integrating with a script tag</AccordionSection.Header.Heading>
              </AccordionSection.Header>
              <ScriptIntegration />
            </AccordionSection>
          </Accordion>
        </ContentContainer>
      </Container>
    </PageContainer>
  );
};
